import { useId, useState } from 'react'
import { Icon } from './Icon'
import { cn } from '../../lib/cn'

export interface AccordionItem {
  id: string
  title: string
  icon?: string
  content: React.ReactNode
  defaultOpen?: boolean
}

interface AccordionProps {
  items: AccordionItem[]
  allowMultiple?: boolean
  className?: string
}

export function Accordion({ items, allowMultiple = true, className }: AccordionProps) {
  const baseId = useId()
  const [open, setOpen] = useState<Set<string>>(
    () => new Set(items.filter((i) => i.defaultOpen).map((i) => i.id)),
  )

  const toggle = (id: string) => {
    setOpen((prev) => {
      const next = new Set(allowMultiple ? prev : [])
      if (prev.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  return (
    <div className={cn('divide-y divide-sand-200 border-y border-sand-200', className)}>
      {items.map((item) => {
        const isOpen = open.has(item.id)
        const btnId = `${baseId}-btn-${item.id}`
        const panelId = `${baseId}-panel-${item.id}`
        return (
          <div key={item.id}>
            <h3 className="m-0">
              <button
                id={btnId}
                aria-expanded={isOpen}
                aria-controls={panelId}
                onClick={() => toggle(item.id)}
                className="flex w-full items-center justify-between gap-4 py-5 text-left transition-colors hover:text-brand-600"
              >
                <span className="flex items-center gap-3 font-display text-lg font-semibold text-ink">
                  {item.icon && <Icon name={item.icon} className="h-5 w-5 text-brand-500" />}
                  {item.title}
                </span>
                <Icon
                  name="ChevronDown"
                  className={cn('h-5 w-5 shrink-0 text-ink-500 transition-transform duration-300', isOpen && 'rotate-180')}
                />
              </button>
            </h3>
            <div
              id={panelId}
              role="region"
              aria-labelledby={btnId}
              className={cn(
                'grid transition-[grid-template-rows] duration-500 ease-premium',
                isOpen ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]',
              )}
            >
              <div className="overflow-hidden">
                <div className="pb-6 pt-0 text-ink-500">{item.content}</div>
              </div>
            </div>
          </div>
        )
      })}
    </div>
  )
}
